/**
 * Detects available microphones (audio input devices) and tracks permission.
 *
 * Device labels are only exposed by the browser after the user has granted
 * microphone permission, so `requestPermission()` opens a short-lived stream to
 * unlock labels, then re-enumerates. Listens for hot-plug via `devicechange`.
 *
 * Where the Permissions API knows the microphone state it is read up front, so
 * a site-level block reads as 'denied' before any prompt is attempted (and
 * flips to 'granted' live when the user re-allows it from the address bar);
 * 'prompt' stays 'unknown' until the user asks.
 */
import { useCallback, useEffect, useState } from 'react';
/**
 * Resolves the microphone PermissionStatus, or null where the API is missing
 * or rejects the 'microphone' name (Firefox), which callers treat as unknown.
 */
async function queryMicPermission() {
    const permissions = typeof navigator !== 'undefined' ? navigator.permissions : undefined;
    if (!permissions?.query)
        return null;
    try {
        return (await permissions.query({
            name: 'microphone',
        }));
    }
    catch {
        return null;
    }
}
const isSupported = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.enumerateDevices;
export function useAudioInputDevices() {
    const [devices, setDevices] = useState([]);
    const [permission, setPermission] = useState(isSupported() ? 'unknown' : 'unsupported');
    const [loading, setLoading] = useState(false);
    const enumerate = useCallback(async () => {
        if (!isSupported()) {
            setPermission('unsupported');
            return;
        }
        setLoading(true);
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            const inputs = all.flatMap((d) => d.kind === 'audioinput'
                ? [
                    {
                        deviceId: d.deviceId,
                        // Labels are blank until permission is granted; the UI supplies a
                        // localized fallback for unlabeled devices.
                        label: d.label,
                    },
                ]
                : []);
            setDevices(inputs);
            // If any label is populated, permission has been granted.
            if (inputs.some((d) => d.label)) {
                setPermission('granted');
            }
        }
        catch {
            // enumerateDevices rarely throws; leave state as-is.
        }
        finally {
            setLoading(false);
        }
    }, []);
    const requestPermission = useCallback(async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setPermission('unsupported');
            return false;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // We only needed the grant, so release the device immediately.
            stream.getTracks().forEach((t) => t.stop());
            setPermission('granted');
            await enumerate();
            return true;
        }
        catch {
            setPermission('denied');
            return false;
        }
    }, [enumerate]);
    useEffect(() => {
        if (!isSupported())
            return;
        const onChange = () => void enumerate();
        // Defer the initial enumeration a tick so the synchronous setLoading at the
        // top of enumerate() doesn't run during the effect's commit phase.
        const id = setTimeout(() => void enumerate(), 0);
        navigator.mediaDevices.addEventListener?.('devicechange', onChange);
        let cancelled = false;
        let status = null;
        const applyStatus = () => {
            if (cancelled || !status)
                return;
            if (status.state === 'denied')
                setPermission('denied');
            else if (status.state === 'granted') {
                setPermission('granted');
                void enumerate();
            }
            else
                setPermission((prev) => (prev === 'denied' ? 'unknown' : prev));
        };
        void queryMicPermission().then((result) => {
            if (cancelled || !result)
                return;
            status = result;
            applyStatus();
            status.addEventListener?.('change', applyStatus);
        });
        return () => {
            cancelled = true;
            clearTimeout(id);
            navigator.mediaDevices.removeEventListener?.('devicechange', onChange);
            status?.removeEventListener?.('change', applyStatus);
        };
    }, [enumerate]);
    return { devices, permission, loading, requestPermission, refresh: enumerate };
}
